import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from './firebase';
import './EmailSetupModal.css';

function EmailSetupModal({ onClose, onSave }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  const [formData, setFormData] = useState({
    useEmailService: false,
    outgoingServer: '',
    port: '',
    useSSL: false,
    username: '',
    password: '',
  });
  
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    loadEmailSettings();
  }, []);

  const loadEmailSettings = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      const franchiseeDoc = await getDoc(franchiseeRef);

      if (franchiseeDoc.exists()) {
        const data = franchiseeDoc.data();
        setFormData({
          useEmailService: data.useEmailService || false,
          outgoingServer: data.outgoingServer || '',
          port: data.port?.toString() || '',
          useSSL: data.useSSL || false,
          username: data.username || '',
          password: data.password || '',
        });
      }
    } catch (error) {
      console.error('Error loading email settings:', error);
      alert('Error loading email settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Auto-update port when SSL is toggled
    if (name === 'useSSL') {
      setFormData(prev => ({
        ...prev,
        port: checked ? '465' : '587'
      }));
    }
  };

  const validateForm = () => {
    if (!formData.useEmailService) {
      return true;
    }
    
    if (!formData.outgoingServer.trim()) {
      alert('Please enter the outgoing server address');
      return false;
    }
    
    if (!formData.port || parseInt(formData.port) <= 0) {
      alert('Please enter a valid port number');
      return false;
    }
    
    if (!formData.username.trim()) {
      alert('Please enter your email username');
      return false;
    }
    
    if (!formData.password) {
      alert('Please enter your email password');
      return false;
    }
    
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setTestResult(null);

    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      
      const updateData = {
        useEmailService: formData.useEmailService,
      };
      
      if (formData.useEmailService) {
        updateData.outgoingServer = formData.outgoingServer.trim();
        updateData.port = parseInt(formData.port);
        updateData.useSSL = formData.useSSL;
        updateData.username = formData.username.trim();
        updateData.password = formData.password;
      }

      await updateDoc(franchiseeRef, updateData);

      alert('Email settings saved successfully!');
      
      if (onSave) {
        onSave();
      }
      
      onClose();
    } catch (error) {
      console.error('Error saving email settings:', error);
      alert('Error saving email settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!validateForm()) {
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const response = await fetch(
        'https://us-central1-babs-2ee3f.cloudfunctions.net/testEmailConfig',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            smtpConfig: {
              host: formData.outgoingServer,
              port: parseInt(formData.port),
              secure: formData.useSSL,
              auth: {
                user: formData.username,
                pass: formData.password
              }
            },
            testEmail: formData.username
          })
        }
      );

      const result = await response.json();
      
      if (result.success) {
        setTestResult({ 
          success: true, 
          message: 'Test email sent successfully! Check your inbox.' 
        });
      } else {
        setTestResult({ 
          success: false, 
          message: result.message || 'Test failed. Please check your settings.' 
        });
      }
    } catch (error) {
      console.error('Error testing email:', error);
      setTestResult({ 
        success: false, 
        message: 'Error testing email configuration.' 
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="email-setup-modal">
          <div className="modal-loading">
            <div className="spinner"></div>
            <p>Loading email settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="email-setup-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📧 Email Configuration</h2>
          <button onClick={onClose} className="close-btn">✕</button>
        </div>

        <div className="modal-body">
          <div className="form-section">
            <div className="toggle-section">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  name="useEmailService"
                  checked={formData.useEmailService}
                  onChange={handleChange}
                  className="toggle-checkbox"
                />
                <span className="toggle-text">Enable Email Service</span>
              </label>
              <p className="toggle-description">
                Configure email settings to send invoices directly to parents/guardians
              </p>
            </div>
          </div>

          {formData.useEmailService && (
            <>
              <div className="info-card">
                <h3>📋 Email Configuration Guide</h3>
                <p>Use the SMTP settings provided by your email provider:</p>
                <ul>
                  <li><strong>Gmail:</strong> smtp.gmail.com, Port 465, SSL enabled</li>
                  <li><strong>Outlook:</strong> smtp.office365.com, Port 587, SSL disabled</li>
                  <li><strong>Custom Domain:</strong> mail.yourdomain.com</li>
                </ul>
                <p className="note">
                  <strong>Note:</strong> Gmail requires app-specific passwords. 
                  Generate one at: myaccount.google.com/apppasswords
                </p>
              </div>

              <div className="form-section">
                <label className="form-label">
                  Outgoing Server (SMTP) *
                  <input
                    type="text"
                    name="outgoingServer"
                    value={formData.outgoingServer}
                    onChange={handleChange}
                    placeholder="mail.yourdomain.com"
                    className="form-input"
                    required
                  />
                </label>
              </div>

              <div className="form-row">
                <div className="form-section half">
                  <label className="form-label">
                    Port *
                    <input
                      type="number"
                      name="port"
                      value={formData.port}
                      onChange={handleChange}
                      placeholder="465 or 587"
                      className="form-input"
                      required
                    />
                  </label>
                </div>

                <div className="form-section half">
                  <label className="toggle-label" style={{ marginTop: '32px' }}>
                    <input
                      type="checkbox"
                      name="useSSL"
                      checked={formData.useSSL}
                      onChange={handleChange}
                      className="toggle-checkbox"
                    />
                    <span className="toggle-text">Use SSL/TLS</span>
                  </label>
                  <p className="field-hint">Enable for port 465</p>
                </div>
              </div>

              <div className="form-section">
                <label className="form-label">
                  Username (Email Address) *
                  <input
                    type="email"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    placeholder="no-reply@yourdomain.com"
                    className="form-input"
                    required
                  />
                </label>
              </div>

              <div className="form-section">
                <label className="form-label">
                  Password *
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="••••••••"
                      className="form-input"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="password-toggle-btn"
                    >
                      {showPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                </label>
              </div>

              {testResult && (
                <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
                  <span className="test-icon">
                    {testResult.success ? '✅' : '❌'}
                  </span>
                  <span>{testResult.message}</span>
                </div>
              )}

              <div className="form-section">
                <button
                  onClick={handleTestEmail}
                  disabled={testing}
                  className="btn-test"
                >
                  {testing ? (
                    <>
                      <div className="spinner-small"></div>
                      Testing...
                    </>
                  ) : (
                    '🧪 Test Email Configuration'
                  )}
                </button>
                <p className="field-hint">
                  This will send a test email to verify your settings
                </p>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-cancel" disabled={saving}>
            Cancel
          </button>
          <button onClick={handleSave} className="btn-save" disabled={saving}>
            {saving ? (
              <>
                <div className="spinner-small"></div>
                Saving...
              </>
            ) : (
              '💾 Save Settings'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EmailSetupModal;